import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import authService from '../services/authService';

const useAuthStore = create(
  persist(
    (set, get) => ({
      // State
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Actions
      login: async (credentials) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.login(credentials);
          set({ 
            user: response.user, 
            isAuthenticated: true, 
            isLoading: false,
            error: null 
          });
          return response;
        } catch (error) {
          // The authService already handles error extraction, so we can use the error message directly
          const errorMessage = typeof error === 'string' ? error : (error.message || 'Login failed');
          
          set({ 
            error: errorMessage, 
            isLoading: false,
            isAuthenticated: false,
            user: null
          });
          throw error;
        }
      },

      register: async (userData) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.register(userData);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          console.error('Registration store error:', error);
          
          // The authService already handles error extraction, so we can use the error message directly
          const errorMessage = typeof error === 'string' ? error : (error.message || 'Registration failed');
          
          set({ 
            error: errorMessage, 
            isLoading: false 
          });
          throw error;
        }
      },

      verifyOTP: async (email, otp) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.verifyOTP(email, otp);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          console.error('OTP verification store error:', error);
          const errorMessage = error.message || 
                              error.detail || 
                              (error.non_field_errors && error.non_field_errors[0]) ||
                              'OTP verification failed';
          set({ 
            error: errorMessage, 
            isLoading: false 
          });
          throw error;
        }
      },

      resendOTP: async (email) => {
        try {
          const response = await authService.resendOTP(email);
          return response;
        } catch (error) {
          console.error('Resend OTP store error:', error);
          const errorMessage = error.message || 
                              error.detail || 
                              (error.non_field_errors && error.non_field_errors[0]) ||
                              'Failed to resend OTP';
          set({ error: errorMessage });
          throw error;
        }
      },

      resetPassword: async (email) => {
        try {
          const response = await authService.resetPassword(email);
          return response;
        } catch (error) {
          console.error('Password reset store error:', error);
          const errorMessage = error.message || 
                              error.detail || 
                              (error.non_field_errors && error.non_field_errors[0]) ||
                              'Failed to reset password';
          set({ error: errorMessage });
          throw error;
        }
      },

      resetPasswordConfirm: async (resetData) => {
        try {
          const response = await authService.resetPasswordConfirm(resetData);
          return response;
        } catch (error) {
          console.error('Password reset confirmation store error:', error);
          const errorMessage = error.message || 
                              error.detail || 
                              (error.non_field_errors && error.non_field_errors[0]) ||
                              'Failed to confirm password reset';
          set({ error: errorMessage });
          throw error;
        }
      },

      logout: () => {
        authService.logout();
        set({ 
          user: null, 
          isAuthenticated: false, 
          error: null 
        });
      },

      updateProfile: async (profileData) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.updateProfile(profileData);
          set({ 
            user: { ...get().user, ...response },
            isLoading: false,
            error: null 
          });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Profile update failed', 
            isLoading: false 
          });
          throw error;
        }
      },

      checkAuth: () => {
        const isAuth = authService.isAuthenticated();
        const user = authService.getCurrentUser();
        set({ 
          isAuthenticated: isAuth, 
          user: user 
        });
      },

      clearError: () => {
        set({ error: null });
      },

      setLoading: (loading) => {
        set({ isLoading: loading });
      },

      update2FAStatus: (enabled) => {
        set((state) => ({
          user: state.user ? { ...state.user, twoFA: enabled } : null
        }));
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ 
        user: state.user, 
        isAuthenticated: state.isAuthenticated 
      }),
    }
  )
);

export default useAuthStore; 